import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "../../utils/languageContext.js";
import { getLocalizedValue, getTranslation } from "../../utils/translate.js";
import {
    deleteProductDish,
    listIntegrations,
    listProductDishes,
    upsertProductDish,
} from "../../services/integration-service.js";
import { getAllMenus } from "../../services/menu-service.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import Button from "../../components/comman/button.js";
import InputField from "../../components/comman/input.js";
import CustomSelect from "../../components/comman/select.js";
import ConfirmationModal from "../../components/Modal/ConfirmationModal.jsx";
import Toast from "../../components/Toast/toast.js";
import ErrorDisplay from "../../components/comman/ErrorDisplay.js";

const Integrations = () => {
    const { language } = useLanguage();
    const toastRef = useRef(null);

    const [configs, setConfigs] = useState([]);
    const [selectedConfigId, setSelectedConfigId] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [dishes, setDishes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [externalProductId, setExternalProductId] = useState("");
    const [dishId, setDishId] = useState("");
    const [confirmDelete, setConfirmDelete] = useState({ open: false, externalProductId: null });

    const selectedConfig = useMemo(
        () => configs.find((config) => String(config.id) === String(selectedConfigId)) || null,
        [configs, selectedConfigId]
    );

    const mappedDishIds = useMemo(
        () => new Set(mappings.map((row) => String(row.dishId))),
        [mappings]
    );

    const dishOptions = useMemo(
        () =>
            dishes
                .filter((dish) => !mappedDishIds.has(String(dish.id)))
                .map((dish) => ({
                    value: String(dish.id),
                    label: `${getLocalizedValue(dish, "name", language) || dish.nameHy || dish.id} (#${dish.id})`,
                })),
        [dishes, language, mappedDishIds]
    );

    const mappingDishLabel = (row) => {
        const fromMap = getLocalizedValue(
            {
                nameEn: row.dishNameEn,
                nameHy: row.dishNameHy,
                nameRu: row.dishNameRu,
            },
            "name",
            language
        );
        return fromMap ? `${fromMap} (#${row.dishId})` : `Dish #${row.dishId}`;
    };

    const loadMappings = useCallback(async (configId) => {
        if (!configId) {
            setMappings([]);
            return;
        }
        const rows = await listProductDishes(configId);
        setMappings(Array.isArray(rows) ? rows : []);
    }, []);

    const loadPage = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [configList, menuData] = await Promise.all([
                listIntegrations(),
                getAllMenus(null, 0, 500),
            ]);
            const nextConfigs = Array.isArray(configList) ? configList : [];
            setConfigs(nextConfigs);
            setDishes(menuData?.dishDtoTreeSet ? Array.from(menuData.dishDtoTreeSet) : []);
            setSelectedConfigId((current) => {
                if (current && nextConfigs.some((config) => String(config.id) === String(current))) {
                    return current;
                }
                return nextConfigs[0]?.id ?? null;
            });
        } catch (loadError) {
            console.error("Failed to load integrations:", loadError);
            setError(loadError);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPage();
    }, [loadPage]);

    useEffect(() => {
        if (!selectedConfigId) {
            setMappings([]);
            return;
        }
        loadMappings(selectedConfigId).catch((loadError) => {
            console.error("Failed to load mappings:", loadError);
            toastRef.current?.addToast(
                getTranslation("integrations.loadMappingsError", language),
                "error"
            );
        });
    }, [selectedConfigId, loadMappings, language]);

    const handleSave = async () => {
        if (!selectedConfigId || !externalProductId.trim() || !dishId) {
            toastRef.current?.addToast(
                getTranslation("integrations.mappingRequired", language),
                "error"
            );
            return;
        }
        setSaving(true);
        try {
            await upsertProductDish(selectedConfigId, {
                externalProductId: externalProductId.trim(),
                dishId: Number(dishId),
            });
            toastRef.current?.addToast(getTranslation("integrations.mappingSaved", language), "success");
            setExternalProductId("");
            setDishId("");
            await loadMappings(selectedConfigId);
        } catch (saveError) {
            toastRef.current?.addToast(
                getTranslation("integrations.mappingSaveError", language),
                "error"
            );
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        const productId = confirmDelete.externalProductId;
        setConfirmDelete({ open: false, externalProductId: null });
        if (!selectedConfigId || !productId) {
            return;
        }
        try {
            await deleteProductDish(selectedConfigId, productId);
            toastRef.current?.addToast(getTranslation("integrations.mappingDeleted", language), "success");
            await loadMappings(selectedConfigId);
        } catch (deleteError) {
            toastRef.current?.addToast(
                getTranslation("integrations.mappingDeleteError", language),
                "error"
            );
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <ErrorDisplay error={error} onRetry={loadPage} />;
    }

    return (
        <section>
            <h1 className="text-xl font-bold text-gray-900 dark:text-[#c9d1d9] mb-4">
                {getTranslation("integrations.pageTitle", language)}
            </h1>
            <p className="text-sm text-gray-600 dark:text-[#8b949e] mb-6">
                {getTranslation("integrations.pageHint", language)}
            </p>

            {configs.length === 0 ? (
                <div className="bg-white dark:bg-[#161b22] border dark:border-[#30363d] rounded-lg p-6 text-gray-600 dark:text-[#8b949e]">
                    {getTranslation("integrations.noConfigs", language)}
                </div>
            ) : (
                <div className="space-y-6">
                    <div className="max-w-md">
                        <CustomSelect
                            label={getTranslation("integrations.config", language)}
                            options={configs.map((config) => ({
                                value: String(config.id),
                                label: `${config.type || "NOPCOMMERCE"} · ${config.serviceUrl || `#${config.id}`}`,
                            }))}
                            value={selectedConfigId ? String(selectedConfigId) : ""}
                            onChange={(option) => setSelectedConfigId(option?.value || null)}
                        />
                    </div>

                    {selectedConfig && (
                        <div className="bg-white dark:bg-[#161b22] border dark:border-[#30363d] rounded-lg p-4">
                            <h2 className="font-semibold text-gray-900 dark:text-[#c9d1d9] mb-4">
                                {getTranslation("integrations.mappingTitle", language)}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <InputField
                                    label={getTranslation("integrations.nopProductId", language)}
                                    value={externalProductId}
                                    onChange={(event) => setExternalProductId(event.target.value)}
                                />
                                <CustomSelect
                                    label={getTranslation("integrations.bychefDish", language)}
                                    options={dishOptions}
                                    value={dishId}
                                    onChange={(option) => setDishId(option?.value || "")}
                                    placeholder={getTranslation("integrations.selectDish", language)}
                                />
                                <div className="flex items-end">
                                    <Button variant="primary" onClick={handleSave} loading={saving} disabled={saving}>
                                        {getTranslation("integrations.saveMapping", language)}
                                    </Button>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-500 dark:text-[#8b949e] border-b dark:border-[#30363d]">
                                            <th className="py-2 pr-4">
                                                {getTranslation("integrations.nopProductId", language)}
                                            </th>
                                            <th className="py-2 pr-4">
                                                {getTranslation("integrations.bychefDish", language)}
                                            </th>
                                            <th className="py-2" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mappings.length === 0 ? (
                                            <tr>
                                                <td colSpan={3} className="py-4 text-gray-500 dark:text-[#8b949e]">
                                                    {getTranslation("integrations.noMappings", language)}
                                                </td>
                                            </tr>
                                        ) : (
                                            mappings.map((row) => (
                                                <tr
                                                    key={`${row.externalProductId}-${row.dishId}`}
                                                    className="border-b last:border-b-0 dark:border-[#30363d]"
                                                >
                                                    <td className="py-3 pr-4 text-gray-900 dark:text-[#c9d1d9]">
                                                        {row.externalProductId}
                                                    </td>
                                                    <td className="py-3 pr-4 text-gray-900 dark:text-[#c9d1d9]">
                                                        {mappingDishLabel(row)}
                                                    </td>
                                                    <td className="py-3 text-right">
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() =>
                                                                setConfirmDelete({
                                                                    open: true,
                                                                    externalProductId: row.externalProductId,
                                                                })
                                                            }
                                                        >
                                                            {getTranslation("common.delete", language)}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {confirmDelete.open && (
                <ConfirmationModal
                    description={getTranslation("integrations.confirmDeleteMapping", language)}
                    onCancel={() => setConfirmDelete({ open: false, externalProductId: null })}
                    onSubmit={handleDelete}
                />
            )}
            <Toast ref={toastRef} />
        </section>
    );
};

export default Integrations;
